import React, {ReactElement} from 'react';
import {useTranslation} from 'react-i18next';
import FontAndBackgroundColorPicker, {COLOR_FIELDS} from '../common/fontAndBackgroundColorPicker';
import SlideChange from '../common/slideChange';
import {IShapeElements} from '../../../../incase-domain/elements/shapes';
import AnimationForm from '../common/animationForm';
import {TABS} from '../../form/part/tabs';
import ChecklistTile from '../../../../incase-domain/components/checkbox/ChecklistTile';
import {useHighlightedElement, useSetHighlightedElement} from '../../../../utils/hooks/slideUpdates/useSetSlideElement';
import {ELEMENT} from '../../../../incase-domain/elements/base/ElementTypes';
import usePresScale from '../../../../ui/util/usePresScale';

interface HighlightedGeometryElementComponentProps {
    activeTab: string;
    withFill?: boolean;
}

HighlightedGeometryElementComponent.defaultProps = {
    withFill: true,
};

function HighlightedGeometryElementComponent(props: HighlightedGeometryElementComponentProps): ReactElement {
    const {withFill, activeTab} = props;
    const {t} = useTranslation();
    const highlightedElement = useHighlightedElement() as IShapeElements;
    const updateHighlightedElement = useSetHighlightedElement();
    const {transformWidthFromPercentage, transformHeightFromPercentage, transformWidthToPercentage, transformHeightToPercentage} =
        usePresScale();

    const handleCircleChange = (isCircle: boolean): void => {
        if (isCircle) {
            const widthInPixels = transformWidthFromPercentage(highlightedElement.width);
            const heightInPixels = transformHeightFromPercentage(highlightedElement.height);
            const sideLength = Math.min(widthInPixels, heightInPixels);
            const sideLengthPercentageWidth = transformWidthToPercentage(sideLength);
            const sideLengthPercentageHeight = transformHeightToPercentage(sideLength);
            updateHighlightedElement.multiple(
                ['width', 'height', 'isCircle'],
                [sideLengthPercentageWidth, sideLengthPercentageHeight, isCircle]
            );
        } else {
            updateHighlightedElement.single('isCircle', isCircle);
        }
    };

    const {type, fill} = highlightedElement;
    const isCircle = type === ELEMENT.ELLIPSE ? highlightedElement.isCircle : false;

    if (activeTab === TABS.STYLE.id) {
        return (
            <>
                <div className="l-group--form">
                    <div className="l-group__item u-flex u-flex-col u-gap-2">
                        {withFill && (
                            <ChecklistTile
                                value={fill}
                                label={t('designSidepanel.filled')}
                                onChange={(value) => updateHighlightedElement.single('fill', value)}
                            />
                        )}
                        <FontAndBackgroundColorPicker
                            highlightedElement={highlightedElement}
                            highlightedElementChangeHandler={updateHighlightedElement}
                            colorFields={[COLOR_FIELDS.BACKGROUND_COLOR]}
                            placeholder={fill ? t('designSidepanel.color') : t('designSidepanel.lineColor')}
                        />
                        {type === ELEMENT.ELLIPSE && (
                            <ChecklistTile value={isCircle} label={t('designSidepanel.isCircle')} onChange={handleCircleChange} />
                        )}
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <AnimationForm
                appearAfter={highlightedElement.appearAfter || 0}
                elementAnimations={highlightedElement.elementAnimations}
                highlightedElementChangeHandler={updateHighlightedElement}
            />
            <div className="l-group--form">
                <SlideChange
                    slideChange={highlightedElement.slideChange}
                    setSlideChange={(slideChange) => {
                        updateHighlightedElement.single('slideChange', slideChange);
                    }}
                />
            </div>
        </>
    );
}

export default HighlightedGeometryElementComponent;
